import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import AppFooter from "@/components/AppFooter";

import { useTranslation } from "react-i18next";
/** Terms of Service page — kept mobile-first and skimmable.
 *  Content is India-focused (governing law, GST, DPDP Act 2023 references).
 *  Update the "Last updated" line whenever material clauses change. */
export default function Terms() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="terms-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <h1 className="ml-auto flex items-center gap-1.5 text-lg font-bold">
            <FileText className="h-4 w-4 text-primary" /> Terms of Service
          </h1>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-8">
        <div className="rounded-2xl border border-border bg-card p-6 text-sm">
          <p className="text-xs text-muted-foreground">Last updated: 18 February 2026 · Effective for all HisabX accounts</p>

          <section className="prose prose-slate mt-6 max-w-none text-sm dark:prose-invert">
            <h2>1. Acceptance of Terms</h2>
            <p>By accessing or using HisabX ("Service", "we", "us"), a product of HisabX Technologies with registered office in Pune, Maharashtra, India, you agree to be bound by these Terms of Service and our <Link to="/privacy" className="text-primary underline">{t("legal.terms.privacy_policy")}</Link>. If you do not accept these Terms, do not create an account or use the Service.</p>

            <h2>2. What HisabX is</h2>
            <p>{t("legal.terms.hisabx_is_a_software_as_a_service_saas_p")}</p>

            <h2>3. Your account</h2>
            <ul>
              <li>{t("legal.terms.you_must_be_at_least_18_years_old_and_le")}</li>
              <li>{t("legal.terms.you_must_register_with_accurate_details")}</li>
              <li>{t("legal.terms.you_are_responsible_for_keeping_your_cre")}</li>
              <li>{t("legal.terms.you_must_promptly_inform_us_of_any_unaut")} <span className="font-mono">security@hisabx.in</span>.</li>
            </ul>

            <h2>4. Ownership of your data</h2>
            <p>{t("legal.terms.all_business_data_you_enter_into_hisabx")} <b>Data · Backup & Reset</b>.</p>

            <h2>5. Acceptable use</h2>
            <ul>
              <li>{t("legal.terms.do_not_use_hisabx_for_illegal_purposes_t")}</li>
              <li>{t("legal.terms.do_not_reverse_engineer_decompile_scrape")}</li>
              <li>{t("legal.terms.do_not_upload_malware_illegal_content_or")}</li>
              <li>{t("legal.terms.photos_uploaded_selfies_slip_images_rece")}</li>
              <li>{t("legal.terms.do_not_use_hisabx_to_send_spam_or_unsoli")}</li>
            </ul>

            <h2>6. Free trial and subscription fees</h2>
            <p>{t("legal.terms.new_dealers_get_a_6_month_free_trial_wit")} <Link to="/pricing" className="text-primary underline">/pricing</Link>. Fees are quoted in INR and exclusive of GST unless stated. Payment failure may result in a grace period followed by feature restriction; we will notify you before any suspension.</p>

            <h2>7. Third-party services</h2>
            <p>{t("legal.terms.hisabx_integrates_with_third_parties_res")}</p>

            <h2>8. AI-generated insights</h2>
            <p>{t("legal.terms.ai_insights_anomalies_expense_hints_prod")}</p>

            <h2>9. Financial records disclaimer</h2>
            <p>{t("legal.terms.the_numbers_hisabx_shows_are_for_your_op")}</p>

            <h2>10. Warranties and liability</h2>
            <p>The Service is provided "as is". We do not warrant that it will be uninterrupted, error-free, or fit for any particular purpose beyond its documented functionality. To the extent permitted by Indian law, our aggregate liability for any claim (contract, tort or otherwise) shall not exceed the fees paid by you to HisabX in the twelve (12) months preceding the claim.</p>

            <h2>11. Suspension &amp; termination</h2>
            <p>Either party may terminate the agreement with 30 days' written notice. We may suspend or terminate immediately if you (a) breach these Terms, (b) engage in fraudulent activity, or (c) fail to pay fees due. On termination you may export your data; we may permanently delete residual data after 90 days.</p>

            <h2>12. Changes to these Terms</h2>
            <p>{t("legal.terms.we_may_update_these_terms_periodically_m")}</p>

            <h2>13. Governing law &amp; jurisdiction</h2>
            <p>{t("legal.terms.these_terms_are_governed_by_the_laws_of")}</p>

            <h2>14. Contact</h2>
            <p>{t("legal.terms.general_queries")} <span className="font-mono">support@hisabx.in</span> · Legal / compliance: <span className="font-mono">legal@hisabx.in</span> · Sales &amp; WhatsApp: <span className="font-mono">+91 70240 45401</span>.</p>
          </section>
        </div>
      </main>

      <AppFooter />
    </div>
  );
}
